import { useEffect, useState } from "react";
import { ItemCategory } from "../models/ItemCategoryTypes";
import {
  createItemCategory,
  deleteItemCategory,
  fetchItemCategoryData,
  updateItemCategory,
} from "../api/ItemCategoryService";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import TableComponent from "../pages/TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/ItemModels.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import { customStyles } from "../styles/common/customFormStyles";

const ItemCategories: React.FC = () => {
  const [itemCategories, setItemCategories] = useState<ItemCategory[]>([]);
  const [currentCategory, setCurrentCategory] = useState<ItemCategory>({
    code: "",
    name: "",
    status: "ACTIVE",
  });

  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    code?: string;
    name?: string;
    status?: string;
  }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => {}
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => {}
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions)

  const [initializeItemCategory, isItemCategoryInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isItemCategoryInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeItemCategory) {
      loadData();
    }
  }, [
    initializeItemCategory,
    currentPage,
    itemsPerPage,
    searchTerm,
    filters,
    sortBy,
    sortOrder,
  ]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      const result: any = await fetchItemCategoryData(params);

      if (result?.data) {
        setItemCategories(result.data.data);
        setTotalItems(result.data.pagination.total);
        setTotalPages(result.data.pagination.totalPages);
      }
    } catch (err) {
      setError("Failed to load categories");
    }
    setIsLoading(false);
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {},
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const validateForm = (): boolean => {
    const errors: { code?: string; name?: string; status?: string } = {};

    if (!currentCategory.code.trim()) {
      errors.code = "Code is required";
    }

    if (!currentCategory.name.trim()) {
      errors.name = "Name is required";
    }

    if (editMode && !currentCategory.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCategory = () => {
    setCurrentCategory({ code: "", name: "", status: "ACTIVE" });
    setValidationErrors({});
    setEditMode(false);
    setIsModalOpen(true);
  };

  const handleEdit = (category: ItemCategory) => {
    setCurrentCategory(category);
    setValidationErrors({}); 
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const categoryToDelete = itemCategories.find(
      (category) => category._id === id
    );

    if (categoryToDelete) {
      configureAlert(
        `Are you sure you want to InActive ${categoryToDelete.name}?`,
        async () => {
          closeAlert(); 

          try {
            await deleteItemCategory(id);
            await loadData();
            configureAlert(
              `Category has been Inactive successfully.`,
              () => {
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          } catch (error) {
            console.error("Error deleting category:", error);
            configureAlert(
              "Failed to delete category.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    if (!validateForm()) {
      return;
    }
  
    setIsSubmitting(true);
    setIsLoading(true);
    
    try {
      let updatedCategory = { ...currentCategory };

      if (editMode) {
        const originalCategory = itemCategories.find(
          (category) => category._id === currentCategory._id
        );
        const hasChanges =
          !originalCategory ||
          originalCategory.code !== currentCategory.code ||
          originalCategory.name !== currentCategory.name ||
          originalCategory.status !== currentCategory.status;

        if (!hasChanges) {
          setIsModalOpen(false);
          configureAlert(
            "No changes were made.",
            closeAlert,
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000
          );
          setIsLoading(false);
          setIsSubmitting(false);
          return;
        }
        await updateItemCategory(
          currentCategory._id as string,
          updatedCategory
        );
        setIsModalOpen(false);
        configureAlert(
          `${currentCategory.name} has been updated successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000 
        );
      } else {
        const response:any = await createItemCategory(updatedCategory);
        setIsModalOpen(false);

        console.log(response);
        
        if(response.data.statusCode === 1400){
          configureAlert(
            `${currentCategory.name} has been added successfully.`,
            () => {
              loadData();
              closeAlert(); 
            },
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000 
          );
        }else{
          configureAlert(
            "Duplicate entry!",
            closeAlert,
            closeAlert,
            "OK",
            "",
            true,
            false,
            3000 
          );
        }
      }
    } catch (err) {
      console.error("Error saving category:", err);
      configureAlert(
       "Duplicate entry!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000 
      );
    } finally {
      setIsLoading(false);
      setIsSubmitting(false); 
    }
  };

  const handleModalClose = () => {
    if (
      isModalOpen &&
      (editMode ||
        currentCategory.code.trim() !== "" ||
        currentCategory.name.trim() !== "")
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null 
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentCategory((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    setCurrentCategory((prev) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "code", label: "Code", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  const statusOptions = [
    { value: "ACTIVE", label: "ACTIVE" },
    { value: "INACTIVE", label: "INACTIVE" },
  ];

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Item Categories</span>
            </div>
            {permission.menus.itemcategorys.create ?
              <div className="items-button-container">
                <button
                  className="item-models-add-btn tooltip-wrapper"
                  onClick={handleAddCategory}
                >
                  <Plus size={16} />
                </button>
              </div>
              : <></>}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="spinner"></div>
      ) : (
        <>
          <div className="box-container">
            <div className="content-wrapper">
              <TableComponent
                columns={columns}
                data={itemCategories}
                onEdit={handleEdit}
                onDelete={handleDelete}
                setSorting={handleSortChange}
                defaultSortConfig={{ key: sortBy, direction: sortOrder }}
                setFilter={(filters: Record<string, any>) => {
                  Object.entries(filters).forEach(([key, value]) => {
                    dispatch(updateFilters({ key, value: String(value) }));
                  });
                }}
              />
              <div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  totalItems={totalItems}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {isModalOpen && (
        <div className="item-models-modal-overlay">
          <div className="item-models-modal">
            <div className="item-models-modal-header">
              <h5>{editMode ? "Edit Category" : "Add New Category"}</h5>
              <button
                className="item-models-modal-close"
                onClick={handleModalClose}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSave} noValidate>
              <div className="form-group">
                <input
                  type="text"
                  name="code"
                  placeholder="Code"
                  value={currentCategory.code}
                  onChange={handleInputChange}
                  className={validationErrors.code ? "input-error" : ""}
                  required
                />
                {validationErrors.code && (
                  <div className="form-error-message">
                    {validationErrors.code}
                  </div>
                )}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={currentCategory.name}
                  onChange={handleInputChange}
                  className={validationErrors.name ? "input-error" : ""}
                  required
                />
                {validationErrors.name && (
                  <div className="form-error-message">
                    {validationErrors.name}
                  </div>
                )}
              </div>
              {editMode && (
                <div className="select-container">
                  <Select
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(
                      (option) => option.value === currentCategory.status
                    )}
                    onChange={handleSelectChange}
                    placeholder="Select Status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                  />
                  {validationErrors.status && (
                    <div className="form-error-message">
                      {validationErrors.status}
                    </div>
                  )}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                className="submit-button"
              >
                {isLoading || isSubmitting ? "Saving..." : editMode ? "Update" : "Add"}
              </button>
            </form>
          </div>
        </div>
      )}
      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default ItemCategories;